import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HeartIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

// Photos live in  frontend/public/team/  with these filenames.
// Until a photo exists, the card shows the person's initials automatically.
// profileUrl (optional): when present, the card links out to that page in a new tab.
// linkLabel (optional): text for that link (defaults to "View profile").
// role (optional): a short emphasized label shown under the name.
const team = [
  {
    name: 'Swati Gupta',
    role: 'Lead MIT PI',
    title: 'Associate Professor',
    affiliation: 'MIT Sloan School of Management',
    image: '/team/swati-gupta.jpg',
    profileUrl: 'https://mitsloan.mit.edu/faculty/directory/swati-gupta',
  },
  {
    name: 'Heidi Yeh',
    role: 'Lead MGB PI',
    title: 'Surgical Director, Pediatric Transplant',
    affiliation: 'Mass General Brigham (MGB)',
    image: '/team/heidi-yeh.jpg',
    profileUrl: 'https://www.massgeneral.org/doctors/18756/heidi-yeh',
  },
  {
    name: 'Korkut Uygun',
    title: 'Professor of Surgery',
    affiliation: 'Harvard Medical School / MGB',
    image: '/team/korkut-uygun.jpg',
    profileUrl: 'https://www.massgeneral.org/surgery/cems/faculty-and-staff/korkut-uygun',
  },
  {
    name: 'Mehmet Toner',
    title: 'Professor of Biomedical Engineering',
    affiliation: 'Harvard Medical School / MGB',
    image: '/team/mehmet-toner.jpg',
    profileUrl: 'https://www.massgeneral.org/surgery/cems/faculty-and-staff/mehmet-toner',
  },
  {
    name: 'Nikolaos Trichakis',
    title: 'Professor of Operations Management',
    affiliation: 'MIT Sloan School of Management',
    image: '/team/nikolaos-trichakis.jpg',
    profileUrl: 'https://mitsloan.mit.edu/faculty/directory/nikos-trichakis',
  },
  {
    name: 'Georgia Perakis',
    title: 'Professor of Operations Management and Operations Research and Statistics',
    affiliation: 'MIT Sloan School of Management',
    image: '/team/georgia-perakis.jpg',
    profileUrl: 'https://mitsloan.mit.edu/faculty/directory/georgia-perakis',
  },
];

const students = [
  {
    name: 'Madeleine Pollack',
    title: 'Graduate Student',
    affiliation: 'Operations Research Center, MIT',
    image: '/team/madeleine-pollack.jpg',
    profileUrl: 'https://orc.mit.edu/students/madeleine-pollack/',
  },
  {
    name: 'Lauren Zhang',
    title: 'Master of Business Analytics',
    affiliation: 'MIT',
    image: '/team/lauren-zhang.jpg',
    profileUrl: 'https://www.linkedin.com/in/luran-zhang/',
  },
];

const alumni = [
  {
    name: 'Thomas Daillak',
    program: 'Masters, Operations Research Center, MIT',
  },
  {
    name: 'Ju Young Lee',
    program: 'Masters of Engineering, Course 6, MIT',
    linkLabel: 'View thesis',
    profileUrl: 'https://dspace.mit.edu/entities/publication/191493ec-29a1-406f-baea-f4a44515d6ef',
  },
  {
    name: 'Prateek Gautam',
    program: 'Master of Business Analytics, Class of 2025, MIT',
  },
];

const Avatar = ({ name, image }) => {
  const [errored, setErrored] = useState(false);
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  if (image && !errored) {
    return (
      <img
        src={image}
        alt={name}
        onError={() => setErrored(true)}
        className="h-24 w-24 rounded-full object-cover mx-auto shadow-md"
      />
    );
  }
  return (
    <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto shadow-md">
      <span className="text-2xl font-bold text-white">{initials}</span>
    </div>
  );
};

const PersonCard = ({ name, role, title, affiliation, image, profileUrl, linkLabel }) => {
  const inner = (
    <>
      <Avatar name={name} image={image} />
      <p className="font-semibold text-gray-900 mt-4">{name}</p>
      {role && (
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mt-1">
          {role}
        </p>
      )}
      {title && <p className="text-sm text-gray-700 mt-1">{title}</p>}
      {affiliation && <p className="text-sm text-gray-500">{affiliation}</p>}
      {profileUrl && (
        <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-3 font-medium">
          {linkLabel || 'View profile'} <ArrowTopRightOnSquareIcon className="h-3 w-3" />
        </span>
      )}
    </>
  );

  const base =
    'bg-white rounded-2xl p-5 border border-gray-200/50 shadow-lg text-center block';

  if (profileUrl) {
    return (
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${base} transition-all hover:shadow-xl hover:-translate-y-1`}
      >
        {inner}
      </a>
    );
  }
  return <div className={base}>{inner}</div>;
};

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Heading */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
              <HeartIcon className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">About This Project</h1>
          </div>

          <div className="text-lg text-gray-600 mb-12 leading-relaxed max-w-3xl space-y-4">
            <p>
              Each day, 18 Americans die waiting for an organ transplant, yet many
              transplantable organs still go unused — in 2024, roughly 22% of recovered
              kidneys were declined and never used, a rate that has risen sharply over
              the past five years. We are trying to change this through a collaborative
              human-AI decision-making pipeline.
            </p>
          </div>

          {/* Team */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Team</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {team.map((p) => (
              <PersonCard key={p.name} {...p} />
            ))}
          </div>

          {/* Students */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Students</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {students.map((p) => (
              <PersonCard key={p.name} {...p} />
            ))}
          </div>

          {/* Alumni */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Alumni</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-12">
            {alumni.map((p) => (
              <li key={p.name}>
                {p.name}{p.program ? ` (${p.program})` : ''}
                {p.profileUrl && (
                  <a
                    href={p.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 ml-2 font-medium"
                  >
                    {p.linkLabel || 'View profile'} <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Funding */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Funding Support</h2>
          <p className="text-gray-700 mb-3 max-w-3xl">We deeply appreciate the support from:</p>
          <ul className="list-disc pl-5 space-y-3 mb-12 max-w-3xl text-gray-800">
            <li>
              <a
                href="https://heals.mit.edu/mit-mgb-seed-program/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                MIT-MGB HEALS Initiative
              </a>{' '}
              — "Reliable and Multi-criteria Human-AI Collaboration for Reduction in
              Organ Non-Utilization."
            </li>
            <li>
              <a
                href="https://computing.mit.edu/news/inaugural-serc-seed-grants-aim-to-advance-ethical-and-responsible-computing-practices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                MIT SERC Seed Grant
              </a>{' '}
              — "Towards Equitable and Efficient Organ Transplantation through Longer
              Preservation Times," led by Swati Gupta, Associate Professor of Operations
              Research and Statistics.
            </li>
          </ul>

          {/* News Coverage */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">News Coverage</h2>
          <ul className="list-disc pl-5 space-y-3 mb-12 max-w-3xl text-gray-800">
            <li>
              <a
                href="https://mitsloan.mit.edu/ideas-made-to-matter/4-ways-to-use-ai-to-improve-lives-mit-researchers"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                4 ways to use AI to improve lives
              </a>{' '}
              — MIT Sloan, Ideas Made to Matter.
            </li>
          </ul>

          {/* Data Usage */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Usage</h2>
          <div className="bg-white rounded-2xl p-6 border border-gray-200/50 shadow-lg flex items-start gap-3 mb-12 max-w-3xl">
            <ShieldCheckIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-gray-700 space-y-3">
              <p>
                This platform is in active development, and access is granted only to
                authorized, approved clinicians. All data is stored securely on a
                dedicated server (AWS EC2) that only the administrators can access.
              </p>
              <p>
                The large language models behind the chat interface are accessed through
                enterprise agreements only. As an added safeguard, these models never see
                the uploaded case data — they work only with the anonymized inputs used by
                the AI prediction models, and serve solely to help interpret model
                outputs. We further constrain them with extensive guardrails that limit
                what they are permitted to communicate.
              </p>
              <p>
                For any data requests, contact{' '}
                <a href="mailto:swatig@mit.edu" className="text-blue-600 underline">Swati Gupta</a>.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-blue-50 rounded-2xl p-5 text-sm text-blue-800 max-w-xl">
            Questions or feedback? Email{' '}
            <a href="mailto:swatig@mit.edu" className="underline">swatig@mit.edu</a>.
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
